import {
  SetterOrUpdater,
  atom,
  useRecoilState,
  useResetRecoilState,
} from 'recoil';
import { useCallback, useMemo } from 'react';
import { merge } from 'lodash-es';
import { AuthState, DeepPartial, User } from 'shared/types';
import { canUserReport, userInGroups } from 'shared/utils';
import { setUseNoCacheHeader } from 'shared/noCacheUtil';
import {
  clearDataForLogout,
  localStorageEffect,
  LOCAL_STORAGE_KEY,
  SESSION_STORAGE_ACCESSKEY_KEY,
} from '../localStorage';

export type PermissionData = {
  canModerate: boolean;
  canReport: boolean;
  isRxOnlyReporter: boolean;
  isInternalUser: boolean;
  isStaffReporter: boolean;
  canCreateLocation: boolean;
  canManageOrganization: boolean;
};

type UseAuthState = {
  isAuthenticated: boolean;
  user: User | null;
  hasActiveMembership: boolean;
  showMembershipFeatures: boolean;
  showMembershipProFeatures: boolean;
  isSupporter: boolean;
  permissions: PermissionData;
  loginSuccess: (
    key: string,
    userData: User,
    hasActiveMembership?: boolean,
  ) => void;
  setAuth: SetterOrUpdater<AuthState>;
  logout: () => void;
  updateAuth: (obj: DeepPartial<AuthState>) => void;
  isProMember: boolean;
  showSupportUs: boolean;
  isOrganizationUser: boolean;
};

export const getDefaultAuthState = (): AuthState => ({
  user: null,
  isAuthenticated: false,
  accessKey: null,
  hasActiveMembership: false,
});

export const authState = atom({
  key: 'AUTH_STATE',
  default: getDefaultAuthState(),
  effects: [localStorageEffect(LOCAL_STORAGE_KEY.AUTH_STATE)],
});

const getPermissions = (user: User | null): PermissionData => {
  const canModerate = userInGroups(user, ['staff_reporters', 'reporters']);
  const canReport = canUserReport(user);
  const canCreateLocation = userInGroups(user, [
    'staff_reporters',
    'reporters',
  ]);
  const isRxOnlyReporter = userInGroups(user, ['rx_reporters']);
  const isInternalUser = userInGroups(user, ['internal_users']);
  const isStaffReporter = userInGroups(user, ['staff_reporters']);
  const canManageOrganization = !!user?.supporterStatus?.canManageOrganization;

  return {
    canModerate,
    canReport,
    isRxOnlyReporter,
    isInternalUser,
    isStaffReporter,
    canCreateLocation,
    canManageOrganization,
  };
};

export const useAuthState = (): UseAuthState => {
  const resetAuthState = useResetRecoilState(authState);
  const [auth, setAuth] = useRecoilState(authState);

  const updateAuth = useCallback(
    (obj: DeepPartial<AuthState>) => {
      setAuth((prev) => merge({}, prev, obj));
    },
    [setAuth],
  );

  const loginSuccess = useCallback(
    (key: string, userData: User, hasActiveMembership?: boolean) => {
      updateAuth({
        isAuthenticated: true,
        user: userData,
        accessKey: key,
        hasActiveMembership,
      });

      // Set "useNoCacheHeader" based on user's reporting permissions
      // This ensures immediate updates when a user logs in, avoiding delays from:
      // 1. Stale values persisting in sessionStorage
      // 2. Slow propagation of Recoil state updates
      setUseNoCacheHeader(canUserReport(userData));

      // Make access token immediately available to getAccessKey() since Recoil updates AUTH_STATE to disk slowly
      sessionStorage.setItem(SESSION_STORAGE_ACCESSKEY_KEY, key);
    },
    [updateAuth],
  );

  const logout = useCallback(() => {
    clearDataForLogout();
    resetAuthState();
    window.location.reload();
  }, [resetAuthState]);

  const hasMembership = useCallback(() => {
    if (!auth.user) return false;

    // the hasActiveMembership key may exist on the auth model for legacy reasons if the user has not logged
    //   back in after we added the supporterStatus attributes
    return (
      auth.hasActiveMembership ||
      Boolean(auth.user?.supporterStatus?.hasActiveMembership)
    );
  }, [auth]);

  const hasActiveMembership = hasMembership();

  const showMembershipFeatures = useMemo(
    () =>
      hasActiveMembership ||
      userInGroups(auth.user, [
        'internal_users',
        'member_benefits',
        'staff_reporters',
        'rx_reporters',
        'reporters',
      ]),
    [auth.user, hasActiveMembership],
  );

  const showMembershipProFeatures =
    (hasActiveMembership && auth.user?.supporterStatus?.planType === 'pro') ||
    userInGroups(auth.user, [
      'internal_users',
      'member_benefits',
      'staff_reporters',
      'rx_reporters',
      'reporters',
    ]);

  const isSupporter = useMemo(
    (): boolean =>
      hasActiveMembership ||
      auth.user?.supporterStatus?.isDonor ||
      userInGroups(auth.user, [
        'internal_users',
        'member_benefits',
        'staff_reporters',
        'rx_reporters',
        'reporters',
      ]),
    [auth.user, hasActiveMembership],
  );
  const permissions = useMemo(() => getPermissions(auth.user), [auth.user]);

  const isProMember =
    hasActiveMembership && auth.user?.supporterStatus?.planType === 'pro';

  const isOrganizationUser = !!auth.user?.supporterStatus?.isOrganizationUser;

  const showSupportUs = !permissions.isInternalUser && !isOrganizationUser;

  return {
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    hasActiveMembership,
    isSupporter,
    showMembershipFeatures,
    showMembershipProFeatures,
    permissions,
    loginSuccess,
    setAuth,
    logout,
    updateAuth,
    isProMember,
    showSupportUs,
    isOrganizationUser,
  };
};
